import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CheckCircle, ChevronRight, ChevronLeft, CalendarDays, Search, Plus, X, MapPin, Clock, Lock, Shield, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHAMPAGNE_OPTS = ["Avec alcool", "Sans alcool", "Non"];

// ── Searchable dropdown with dynamic creation ──────────────────────────────────
function SearchableDropdown({ value, onChange, items, placeholder, onAddNew, addLabel = "Ajouter" }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = items.filter(i => i.name.toLowerCase().includes(query.toLowerCase()));
  const exactMatch = items.find(i => i.name.toLowerCase() === query.toLowerCase());
  const showAdd = query.trim().length > 1 && !exactMatch && onAddNew;

  const select = (name) => { onChange(name); setQuery(name); setOpen(false); };
  const handleAdd = async () => {
    const added = await onAddNew(query.trim());
    if (added) select(added.name);
  };

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full border border-gray-200 pl-9 pr-8 py-3 text-sm focus:outline-none focus:border-[#D4A843] transition-colors"
        />
        {value && (
          <button onClick={() => { onChange(""); setQuery(""); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X size={14} />
          </button>
        )}
      </div>

      {open && (filtered.length > 0 || showAdd) && (
        <div className="absolute z-50 top-full left-0 right-0 bg-white border border-gray-200 shadow-lg max-h-52 overflow-y-auto">
          {filtered.map(i => (
            <button key={i.id} type="button"
              onClick={() => select(i.name)}
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-amber-50 hover:text-[#D4A843] transition-colors ${value === i.name ? "bg-amber-50 text-[#D4A843] font-semibold" : "text-gray-700"}`}>
              {i.name}
            </button>
          ))}
          {showAdd && (
            <button type="button" onClick={handleAdd}
              className="w-full text-left px-4 py-2.5 text-sm text-[#D4A843] font-semibold hover:bg-amber-50 border-t border-gray-100 flex items-center gap-2">
              <Plus size={14} /> {addLabel} « {query.trim()} »
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Simple text searchable dropdown (for lieu/salle) ──────────────────────────
function SearchableCreate({ value, onChange, items, placeholder, apiEndpoint }) {
  const addNew = useCallback(async (name) => {
    try {
      const res = await axios.post(`${API}/${apiEndpoint}/public`, { name });
      return res.data;
    } catch {
      // If no public endpoint, just return as-is
      return { id: name, name };
    }
  }, [apiEndpoint]);

  return (
    <SearchableDropdown
      value={value}
      onChange={onChange}
      items={items}
      placeholder={placeholder}
      onAddNew={addNew}
      addLabel="Ajouter"
    />
  );
}

// ── Theme specific: uses /themes/suggest public endpoint ─────────────────────
function ThemeSelector({ value, onChange, themes, onNewTheme }) {
  const addNew = useCallback(async (name) => {
    try {
      const res = await axios.post(`${API}/themes/suggest`, { name });
      if (onNewTheme) onNewTheme(res.data);
      return res.data;
    } catch {
      return { id: name, name };
    }
  }, [onNewTheme]);

  return (
    <SearchableDropdown
      value={value}
      onChange={onChange}
      items={themes}
      placeholder="Rechercher ou saisir votre thème..."
      onAddNew={addNew}
      addLabel="Suggérer le thème"
    />
  );
}

export default function ReservationPage() {
  const { vehicleId } = useParams();
  const [vehicles, setVehicles] = useState([]);
  const [themes, setThemes] = useState([]);
  const [lieux, setLieux] = useState([]);
  const [salles, setSalles] = useState([]);
  const [dateAvailability, setDateAvailability] = useState(null);
  const [checkingDate, setCheckingDate] = useState(false);
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [devisCreated, setDevisCreated] = useState(null);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    vehicle_id: vehicleId || "",
    date_mariage: "",
    client_name: "",
    client_email: "",
    client_telephone: "",
    client_ville: "",
    client_adresse: "",
    client_code_postal: "",
    prenom_conjoint: "",
    contact_preference: "",
    disponibilite_contact: "",
    // Détails événement
    lieu_evenement: "",
    lieu_ceremonie: "",
    salle_reception: "",
    theme_decoration: "",
    couleurs_decoration: "",
    formule_decoration: "",
    type_ceremonie: "",
    nombre_passagers: "",
    champagne: "",
    notes: "",
    // Logistique
    adresse_depart: "",
    heure_depart: "",
    adresse_mairie: "",
    horaire_mairie: "",
    adresse_eglise: "",
    horaire_eglise: "",
    heure_fin_souhaitee: "",
    trajet_retour: "",
    arrets_photos: "",
    lieu_photos: "",
    consentement: false,
  });

  useEffect(() => {
    axios.get(`${API}/vehicles`).then(r => setVehicles(r.data.filter(v => v.is_available !== false))).catch(() => {});
    axios.get(`${API}/themes`).then(r => setThemes(r.data)).catch(() => {});
    axios.get(`${API}/lieux`).then(r => setLieux(r.data)).catch(() => {});
    axios.get(`${API}/salles`).then(r => setSalles(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!form.date_mariage) {
      setDateAvailability(null);
      setCheckingDate(false);
      return () => { cancelled = true; };
    }
    setCheckingDate(true);
    setDateAvailability(null);
    axios.get(`${API}/availability`, { params: { date_mariage: form.date_mariage } })
      .then(r => {
        if (!cancelled) setDateAvailability({ available: r.data.available, error: false });
      })
      .catch(error => {
        if (cancelled) return;
        const unavailable = error.response?.status === 409;
        setDateAvailability({ available: false, error: !unavailable });
      })
      .finally(() => { if (!cancelled) setCheckingDate(false); });
    return () => { cancelled = true; };
  }, [form.date_mariage]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectedVehicle = vehicles.find(v => v.id === form.vehicle_id);
  const isDateConflict = form.date_mariage && (
    dateAvailability?.available === false && !dateAvailability?.error
  );
  const isDateCheckError = form.date_mariage && dateAvailability?.error;
  const acompteAmount = selectedVehicle ? Math.min(200, selectedVehicle.price) : 0;

  // ── Écran de paiement après création du devis ─────────────────────────────
  if (devisCreated && !success) return (
    <div className="font-sans min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 bg-[#f8f5ef] py-16">
        <div className="max-w-lg mx-auto px-4">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Demande envoyée !</h2>
            <p className="text-sm text-gray-500">N° {devisCreated.numero} — Récapitulatif envoyé à <strong>{form.client_email}</strong></p>
          </div>

          <div className="bg-white p-8 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Confirmer votre date maintenant</h3>
            <p className="text-sm text-gray-400 mb-6">Sécurisez votre réservation en versant un acompte de 200 €. Votre date est confirmée immédiatement.</p>

            <div className="bg-amber-50 border border-amber-200 p-4 mb-6 flex justify-between items-center">
              <div>
                <p className="text-xs text-amber-600 uppercase tracking-wider mb-1">Acompte</p>
                <p className="text-2xl font-bold text-amber-700">{acompteAmount}€</p>
                <p className="text-xs text-gray-400 mt-0.5">Solde de {selectedVehicle?.price - acompteAmount}€ le jour J</p>
              </div>
              <div className="text-right text-sm text-gray-400">
                <p>Total</p>
                <p className="text-lg font-semibold text-gray-700">{selectedVehicle?.price}€</p>
              </div>
            </div>

            <button
              data-testid="pay-acompte-btn"
              onClick={() => { window.location.href = `/paiement?devis=${devisCreated.id}&type=acompte`; }}
              className="w-full py-4 bg-[#D4A843] text-white font-bold tracking-wider hover:bg-[#c49933] transition-colors mb-3 flex items-center justify-center gap-2"
            >
              <Lock size={16} /> PAYER L'ACOMPTE — {acompteAmount}€
            </button>

            <button
              data-testid="pay-later-btn"
              onClick={() => setSuccess(true)}
              className="w-full py-3 text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Je paierai plus tard
            </button>

            <div className="flex items-center gap-2 justify-center mt-5 text-xs text-gray-300">
              <Shield size={12} /> Paiement sécurisé par Stripe
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );

  const canNext1 = form.vehicle_id && form.date_mariage && dateAvailability?.available === true && !checkingDate;
  const canNext2 = form.client_name && form.client_email && form.client_telephone && form.client_ville &&
    form.client_adresse && form.client_code_postal && form.contact_preference && form.disponibilite_contact;
  const canNext3 = form.lieu_evenement && form.lieu_ceremonie && form.salle_reception &&
    form.theme_decoration && form.couleurs_decoration && form.formule_decoration &&
    form.type_ceremonie && form.nombre_passagers && form.champagne;
  const needsTownhall = ["Mairie", "Mairie + cérémonie"].includes(form.type_ceremonie);
  const needsCeremony = ["Religieuse", "Laïque", "Mairie + cérémonie"].includes(form.type_ceremonie);
  const canNext4 = form.adresse_depart && form.heure_depart && form.heure_fin_souhaitee &&
    form.trajet_retour && form.arrets_photos &&
    (!needsTownhall || (form.adresse_mairie && form.horaire_mairie)) &&
    (!needsCeremony || (form.adresse_eglise && form.horaire_eglise));
  const canSubmit = form.consentement;

  const submit = async () => {
    setLoading(true);
    try {
      const complements = [
        form.notes?.trim(),
        "INFORMATIONS COMPLÈTES DU FORMULAIRE",
        `Conjoint(e) : ${form.prenom_conjoint || "Non renseigné"}`,
        `Adresse de facturation : ${form.client_adresse}, ${form.client_code_postal} ${form.client_ville}`,
        `Contact souhaité : ${form.contact_preference}, disponibilité : ${form.disponibilite_contact}`,
        `Type de cérémonie : ${form.type_ceremonie}`,
        `Nombre de passagers : ${form.nombre_passagers}`,
        `Décoration : ${form.formule_decoration}, couleurs : ${form.couleurs_decoration}`,
        `Fin souhaitée : ${form.heure_fin_souhaitee}, trajet retour : ${form.trajet_retour}`,
        `Arrêts photos : ${form.arrets_photos}${form.lieu_photos ? `, lieu : ${form.lieu_photos}` : ""}`,
      ].filter(Boolean).join("\n");
      const res = await axios.post(`${API}/devis`, {
        ...form,
        notes: complements,
      });
      setDevisCreated(res.data);
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de l'envoi du devis. Veuillez nous contacter directement : 0692 04 20 73");
    } finally {
      setLoading(false);
    }
  };

  const STEPS = [
    "Véhicule & Date",
    "Vos coordonnées",
    "Détails événement",
    "Logistique",
    "Validation",
  ];

  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-0 mb-10">
      {STEPS.map((label, i) => {
        const n = i + 1;
        return (
          <div key={n} className="flex items-center">
            <div className={`flex items-center gap-2 px-3 py-2 ${n <= step ? "opacity-100" : "opacity-40"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold transition-all ${n < step ? "bg-[#D4A843] text-white" : n === step ? "bg-[#1a1a1a] text-white" : "bg-gray-200 text-gray-500"}`}>
                {n < step ? <CheckCircle size={14} /> : n}
              </div>
              <span className="hidden sm:block text-xs font-medium text-gray-600">{label}</span>
            </div>
            {i < STEPS.length - 1 && <div className="w-8 h-px bg-gray-200" />}
          </div>
        );
      })}
    </div>
  );

  if (success) return (
    <div className="font-sans min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center bg-[#f8f5ef]">
        <div className="text-center max-w-md mx-auto px-4 py-16">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-500" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Demande enregistrée !</h2>
          <p className="text-gray-500 mb-3">Votre demande a bien été reçue. Nous vous contacterons sous 24h pour confirmer la disponibilité.</p>
          <p className="text-sm text-gray-400 mb-3">N° {devisCreated?.numero} — Récapitulatif envoyé à <strong>{form.client_email}</strong></p>
          <p className="text-xs text-gray-400 mb-8">Vous pourrez payer l'acompte via le lien que nous vous enverrons par email.</p>
          <a href="/" className="inline-block px-8 py-3 bg-[#D4A843] text-white font-semibold tracking-wider hover:bg-[#c49933] transition-colors">
            Retour à l'accueil
          </a>
        </div>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="font-sans min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 bg-[#f8f5ef]">
        <section className="py-12 text-center">
          <p className="text-xs font-semibold tracking-[0.3em] text-[#D4A843] uppercase mb-2">Réservation</p>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Demande de devis</h1>
          <p className="text-gray-500">Remplissez le formulaire ci-dessous et recevez votre devis personnalisé.</p>
        </section>

        <div className="max-w-2xl mx-auto px-4 pb-16">
          <StepIndicator />

          <div className="bg-white p-8 shadow-sm">

            {/* ── STEP 1 : Véhicule & Date ── */}
            {step === 1 && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-6">Choisissez votre véhicule *</h2>
                <div className="grid grid-cols-1 gap-4 mb-8">
                  {vehicles.map(v => (
                    <button key={v.id} data-testid={`select-vehicle-${v.id}`}
                      onClick={() => set("vehicle_id", v.id)}
                      className={`overflow-hidden border-2 text-left transition-all sm:flex ${form.vehicle_id === v.id ? "border-[#D4A843] shadow-md" : "border-gray-200 hover:border-gray-300"}`}>
                      <div className="h-52 sm:h-44 sm:w-3/5 overflow-hidden bg-gray-100">
                        {v.images?.[0] ? (
                          <img src={v.images[0]} alt={v.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
                            {v.name}
                          </div>
                        )}
                      </div>
                      <div className="p-5 flex-1 flex flex-col justify-center">
                        <p className="text-xs uppercase tracking-widest text-[#D4A843] font-semibold mb-2">Votre voiture mariage</p>
                        <p className="font-bold text-gray-900 text-lg">{v.name}</p>
                        <p className="text-gray-400 text-xs mt-1 mb-3">Chauffeur, décoration et champagne inclus</p>
                        <p className="text-[#D4A843] font-bold text-xl">{v.price}€ TTC</p>
                      </div>
                    </button>
                  ))}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <CalendarDays size={14} className="inline mr-1" /> Date du mariage *
                  </label>
                  <input type="date" value={form.date_mariage}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={e => set("date_mariage", e.target.value)}
                    data-testid="date-mariage-input"
                    className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4A843] transition-colors" />
                  {isDateConflict && (
                    <div className="mt-2 flex items-start gap-2 bg-red-50 border border-red-200 px-3 py-2 rounded text-xs text-red-700">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0 text-red-500" />
                      <span>Cette date est déjà réservée. Merci de choisir une autre date.</span>
                    </div>
                  )}
                  {isDateCheckError && (
                    <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 px-3 py-2 rounded text-xs text-amber-700">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-500" />
                      <span>La disponibilité ne peut pas être vérifiée pour le moment. Réessayez dans quelques instants.</span>
                    </div>
                  )}
                  {checkingDate && (
                    <p className="text-xs text-gray-400 mt-2">Vérification de la disponibilité…</p>
                  )}
                  {!checkingDate && dateAvailability?.available && (
                    <p className="text-xs text-green-600 mt-2">Cette date est disponible.</p>
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 2 : Coordonnées ── */}
            {step === 2 && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-6">Vos coordonnées</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    ["client_name", "Nom complet *", "text", "Marie Dupont", "sm:col-span-2"],
                    ["prenom_conjoint", "Prénom du/de la conjoint(e)", "text", "Thomas", "sm:col-span-2"],
                    ["client_email", "Email *", "email", "marie@email.com", ""],
                    ["client_telephone", "Téléphone *", "tel", "0692 04 20 73", ""],
                    ["client_adresse", "Adresse de facturation *", "text", "12 rue des Fleurs", "sm:col-span-2"],
                    ["client_code_postal", "Code postal *", "text", "97400", ""],
                    ["client_ville", "Ville *", "text", "Saint-Denis", ""],
                  ].map(([key, label, type, placeholder, cls]) => (
                    <div key={key} className={cls}>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
                      <input type={type} placeholder={placeholder} value={form[key]}
                        onChange={e => set(key, e.target.value)}
                        data-testid={`input-${key}`}
                        className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4A843] transition-colors" />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5 pt-5 border-t border-gray-100">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Comment préférez-vous être contacté(e) ? *</label>
                    <select value={form.contact_preference} onChange={e => set("contact_preference", e.target.value)}
                      className="w-full border border-gray-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#D4A843]">
                      <option value="">Choisir...</option>
                      <option>Téléphone</option><option>WhatsApp</option><option>Email</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Meilleur moment pour vous joindre *</label>
                    <select value={form.disponibilite_contact} onChange={e => set("disponibilite_contact", e.target.value)}
                      className="w-full border border-gray-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#D4A843]">
                      <option value="">Choisir...</option>
                      <option>Matin, 8h à 12h</option><option>Après-midi, 12h à 17h</option><option>Soir, après 17h</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 3 : Détails événement ── */}
            {step === 3 && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-6">Détails de votre mariage</h2>
                <div className="space-y-5">

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Type de cérémonie *</label>
                      <select value={form.type_ceremonie} onChange={e => set("type_ceremonie", e.target.value)}
                        className="w-full border border-gray-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#D4A843]">
                        <option value="">Choisir...</option>
                        <option>Mairie</option><option>Religieuse</option><option>Laïque</option><option>Mairie + cérémonie</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Nombre de passagers dans la voiture *</label>
                      <select value={form.nombre_passagers} onChange={e => set("nombre_passagers", e.target.value)}
                        className="w-full border border-gray-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#D4A843]">
                        <option value="">Choisir...</option>
                        <option>1 personne</option><option>2 personnes</option><option>3 personnes</option><option>4 personnes ou plus</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <MapPin size={13} className="inline mr-1 text-[#D4A843]" /> Commune de l'événement *
                      </label>
                      <SearchableCreate value={form.lieu_evenement} onChange={v => set("lieu_evenement", v)}
                        items={lieux} placeholder="Saint-Denis, Saint-Pierre..." apiEndpoint="lieux" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <MapPin size={13} className="inline mr-1 text-[#D4A843]" /> Lieu exact de la cérémonie *
                      </label>
                      <input type="text" value={form.lieu_ceremonie} onChange={e => set("lieu_ceremonie", e.target.value)}
                        placeholder="Mairie, église, domaine..."
                        className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4A843]" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <MapPin size={13} className="inline mr-1 text-[#D4A843]" /> Salle ou lieu de réception *
                    </label>
                    <SearchableCreate value={form.salle_reception} onChange={v => set("salle_reception", v)}
                      items={salles} placeholder="Salle Camélias, domaine, adresse privée..." apiEndpoint="salles" />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Thème du mariage *</label>
                    <ThemeSelector
                      value={form.theme_decoration}
                      onChange={v => set("theme_decoration", v)}
                      themes={themes}
                      onNewTheme={t => setThemes(prev => [...prev.filter(p => p.id !== t.id), t])}
                    />
                    <p className="text-xs text-gray-400 mt-1.5">
                      Si votre thème n'est pas dans la liste, tapez-le et cliquez « Suggérer ». Nous le validerons.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Décoration souhaitée *</label>
                      <select value={form.formule_decoration} onChange={e => set("formule_decoration", e.target.value)}
                        className="w-full border border-gray-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#D4A843]">
                        <option value="">Choisir...</option>
                        <option>Décoration standard incluse</option><option>Décoration personnalisée</option><option>Sans décoration</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Couleurs / fleurs souhaitées *</label>
                      <input type="text" value={form.couleurs_decoration} onChange={e => set("couleurs_decoration", e.target.value)}
                        placeholder="Blanc et or, fleurs rouges, à définir..."
                        className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4A843]" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">Champagne *</label>
                    <div className="flex flex-wrap gap-2">
                      {CHAMPAGNE_OPTS.map(o => (
                        <button key={o} type="button" data-testid={`champagne-${o}`}
                          onClick={() => set("champagne", o)}
                          className={`py-2.5 px-6 text-sm border-2 transition-all ${form.champagne === o ? "border-[#D4A843] bg-amber-50 font-semibold text-gray-900" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* ── STEP 4 : Logistique ── */}
            {step === 4 && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-2">Logistique du jour J</h2>
                <p className="text-sm text-gray-400 mb-6">Renseignez le parcours le plus précisément possible. Choisissez « À confirmer » lorsqu'un détail n'est pas encore arrêté.</p>
                <div className="space-y-6">

                  {/* Départ */}
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Clock size={13} className="text-[#D4A843]" /> Départ
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Adresse de départ</label>
                        <input type="text" value={form.adresse_depart}
                          onChange={e => set("adresse_depart", e.target.value)}
                          data-testid="input-adresse-depart"
                          placeholder="Ex: 12 Rue des Fleurs, Saint-Denis"
                          className="w-full border border-gray-200 px-3 py-3 text-sm focus:outline-none focus:border-[#D4A843]" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Heure de départ</label>
                        <input type="time" value={form.heure_depart}
                          onChange={e => set("heure_depart", e.target.value)}
                          data-testid="input-heure-depart"
                          className="w-full border border-gray-200 px-3 py-3 text-sm focus:outline-none focus:border-[#D4A843]" />
                      </div>
                    </div>
                  </div>

                  {/* Mairie */}
                  {needsTownhall && <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <MapPin size={13} className="text-[#D4A843]" /> Mairie
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Adresse de la mairie</label>
                        <input type="text" value={form.adresse_mairie}
                          onChange={e => set("adresse_mairie", e.target.value)}
                          data-testid="input-adresse-mairie"
                          placeholder="Ex: Mairie de Saint-Denis"
                          className="w-full border border-gray-200 px-3 py-3 text-sm focus:outline-none focus:border-[#D4A843]" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Horaire mairie</label>
                        <input type="time" value={form.horaire_mairie}
                          onChange={e => set("horaire_mairie", e.target.value)}
                          data-testid="input-horaire-mairie"
                          className="w-full border border-gray-200 px-3 py-3 text-sm focus:outline-none focus:border-[#D4A843]" />
                      </div>
                    </div>
                  </div>}

                  {/* Église */}
                  {needsCeremony && <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <MapPin size={13} className="text-[#D4A843]" /> Cérémonie religieuse ou laïque
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Adresse exacte</label>
                        <input type="text" value={form.adresse_eglise}
                          onChange={e => set("adresse_eglise", e.target.value)}
                          data-testid="input-adresse-eglise"
                          placeholder="Ex: Cathédrale, domaine, plage..."
                          className="w-full border border-gray-200 px-3 py-3 text-sm focus:outline-none focus:border-[#D4A843]" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Horaire cérémonie</label>
                        <input type="time" value={form.horaire_eglise}
                          onChange={e => set("horaire_eglise", e.target.value)}
                          data-testid="input-horaire-eglise"
                          className="w-full border border-gray-200 px-3 py-3 text-sm focus:outline-none focus:border-[#D4A843]" />
                      </div>
                    </div>
                  </div>}

                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Clock size={13} className="text-[#D4A843]" /> Fin de prestation et retour
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Heure de fin souhaitée *</label>
                        <input type="time" value={form.heure_fin_souhaitee} onChange={e => set("heure_fin_souhaitee", e.target.value)}
                          className="w-full border border-gray-200 px-3 py-3 text-sm focus:outline-none focus:border-[#D4A843]" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Trajet retour nécessaire ? *</label>
                        <select value={form.trajet_retour} onChange={e => set("trajet_retour", e.target.value)}
                          className="w-full border border-gray-200 px-3 py-3 text-sm bg-white focus:outline-none focus:border-[#D4A843]">
                          <option value="">Choisir...</option><option>Oui</option><option>Non</option><option>À confirmer</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <MapPin size={13} className="text-[#D4A843]" /> Photos avec le véhicule
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Arrêt photo souhaité ? *</label>
                        <select value={form.arrets_photos} onChange={e => set("arrets_photos", e.target.value)}
                          className="w-full border border-gray-200 px-3 py-3 text-sm bg-white focus:outline-none focus:border-[#D4A843]">
                          <option value="">Choisir...</option><option>Oui</option><option>Non</option><option>À définir</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Lieu souhaité</label>
                        <input type="text" value={form.lieu_photos} onChange={e => set("lieu_photos", e.target.value)}
                          placeholder="Plage, point de vue, domaine..."
                          disabled={form.arrets_photos === "Non"}
                          className="w-full border border-gray-200 px-3 py-3 text-sm focus:outline-none focus:border-[#D4A843] disabled:bg-gray-50" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 5 : Validation ── */}
            {step === 5 && selectedVehicle && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-2">Vérifiez votre demande</h2>
                <p className="text-sm text-gray-400 mb-6">Ces informations seront reprises dans votre devis. Vous pourrez revenir en arrière avant l'envoi.</p>
                <div className="bg-amber-50 border border-amber-100 p-5 mb-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <div><span className="text-gray-400">Véhicule</span><p className="font-semibold">{selectedVehicle.name}</p></div>
                    <div><span className="text-gray-400">Date</span><p className="font-semibold">{form.date_mariage}</p></div>
                    <div><span className="text-gray-400">Client</span><p className="font-semibold">{form.client_name}</p></div>
                    <div><span className="text-gray-400">Contact</span><p className="font-semibold">{form.contact_preference}, {form.disponibilite_contact}</p></div>
                    <div><span className="text-gray-400">Cérémonie</span><p className="font-semibold">{form.type_ceremonie}, {form.lieu_ceremonie}</p></div>
                    <div><span className="text-gray-400">Réception</span><p className="font-semibold">{form.salle_reception}</p></div>
                    <div><span className="text-gray-400">Décoration</span><p className="font-semibold">{form.formule_decoration}, {form.couleurs_decoration}</p></div>
                    <div><span className="text-gray-400">Horaires</span><p className="font-semibold">Départ {form.heure_depart}, fin {form.heure_fin_souhaitee}</p></div>
                  </div>
                  <div className="flex justify-between items-center mt-5 pt-4 border-t border-amber-200">
                    <span className="text-gray-500 text-sm">Total estimé TTC</span>
                    <span className="text-xl font-bold text-[#D4A843]">{selectedVehicle.price}€</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Dernières précisions ou demandes particulières</label>
                  <textarea rows={4} placeholder="Accessibilité, contraintes, personne à prévenir, détails utiles..." value={form.notes}
                    onChange={e => set("notes", e.target.value)} data-testid="input-notes"
                    className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4A843] resize-none" />
                </div>
                <label className="mt-5 flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={form.consentement} onChange={e => set("consentement", e.target.checked)}
                    className="mt-1 accent-[#D4A843]" />
                  <span>Je confirme l'exactitude des informations et j'accepte d'être contacté(e) pour finaliser cette prestation. *</span>
                </label>
              </div>
            )}

            {/* ── Navigation ── */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
              {step > 1 ? (
                <button onClick={() => setStep(s => s - 1)} data-testid="prev-step-btn"
                  className="flex items-center gap-2 px-6 py-3 border-2 border-gray-200 text-gray-700 text-sm font-semibold hover:border-gray-400 transition-colors">
                  <ChevronLeft size={16} /> Précédent
                </button>
              ) : <div />}

              {step < 5 ? (
                <button onClick={() => setStep(s => s + 1)}
                  disabled={step === 1 ? !canNext1 : step === 2 ? !canNext2 : step === 3 ? !canNext3 : !canNext4}
                  data-testid="next-step-btn"
                  className="flex items-center gap-2 px-8 py-3 bg-[#1a1a1a] text-white text-sm font-semibold tracking-wider hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  SUIVANT <ChevronRight size={16} />
                </button>
              ) : (
                <button onClick={submit} disabled={loading || !canSubmit} data-testid="submit-devis-btn"
                  className="flex items-center gap-2 px-10 py-3 bg-[#D4A843] text-white text-sm font-semibold tracking-wider hover:bg-[#c49933] transition-colors disabled:opacity-60">
                  {loading ? "Envoi en cours..." : "ENVOYER MON DEVIS"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
